import noEventsImg from "assets/img/no-events.svg"

import React, { FunctionComponent, useMemo, useCallback, useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"
import { Guid } from "@/types"
import Text from "components/Text/Text"
import useAssetReservations from "hooks/useAssetReservations"
import SchedulerEvent from "components/Scheduler/SchedulerEvent"
import { getPurposeLabel, nowWithoutTimezone } from "utils/utils"
import reservationSidebars from "stores/reservationSidebars"
import { MAX_ITEMS_PER_PAGE, UserPermission } from "config/constants"
import Spinner from "components/Spinner/Spinner"
import SettingsBlock from "components/SettingsBlock/SettingsBlock"
import Button from "components/Button/Button"
import AssetReservationPaged from "models/AssetReservationPaged"
import Routes from "@/routes"
import useAssetInspections from "hooks/useAssetInspections"
import Asset from "models/Asset"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import { usePermissions } from "stores/permissions"
import { getSchedulerEventTypeByReservationPurpose } from "components/Scheduler/SchedulerUtils"

interface IReservationsProps {
    asset: Asset
    isMachine: boolean
    onDeleteReservation?(): void
    onFailLoadReservations?(): void
    /** Optional: override the asset ID used for fetching reservations (for version support) */
    overrideAssetId?: Guid
    /** When true, hides creation controls - used for historical versions */
    readOnly?: boolean
}

const Reservations: FunctionComponent<React.PropsWithChildren<IReservationsProps>> = ({
    asset,
    isMachine,
    onDeleteReservation,
    onFailLoadReservations,
    overrideAssetId,
    readOnly = false,
}) => {
    const { t } = useTranslation()
    const location = useLocation()
    const { hasPermission } = usePermissions()
    const canCreateReservation = hasPermission(UserPermission.ReservationEdit) && !readOnly
    const effectiveAssetId = overrideAssetId ?? asset.id!
    const reservationsParams = useMemo(() => {
        return {
            assetIds: [effectiveAssetId],
            pageSize: MAX_ITEMS_PER_PAGE,
        }
    }, [effectiveAssetId])

    const [isLoadingFailed, setIsLoadingFailed] = useState(false)
    const handleLoadingFail = useCallback(() => {
        setIsLoadingFailed(true)
        if (typeof onFailLoadReservations === "function") {
            onFailLoadReservations()
        }
    }, [onFailLoadReservations])

    const {
        assetReservations,
        reload: reloadReservations,
    } = useAssetReservations(reservationsParams, handleLoadingFail)

    useEffect(() => {
        if (!assetReservations) return
        setIsLoadingFailed(false)
    }, [assetReservations])

    const {
        assetInspections,
        assetInspectionsGrouped,
        fetchAssetInspections,
        getLatestInspections,
        globalLatestAssetInspection,
    } = useAssetInspections({
        assetId: effectiveAssetId,
        groupBy: "reservationId",
    })

    useEffect(() => {
        if (!assetReservations || assetReservations.length === 0) return
        fetchAssetInspections()
    }, [assetReservations, fetchAssetInspections])

    // looking for a reservation which duration intersects with the current moment of time
    const currentReservation = useMemo<{ event: AssetReservationPaged, progress: number } | null>(() => {
        const now = nowWithoutTimezone()
        const event = assetReservations?.find(ev => ev.eventStart?.isBefore(now) && (!ev.eventEnd || ev.eventEnd.isAfter(now)))
        if (!event) {
            return null
        }

        let progress = 0
        if (event.eventEnd) {
            progress = (now.diff(event.eventStart, "minutes") / event.eventEnd.diff(event.eventStart, "minutes")) * 100
        }

        return {
            event,
            progress
        }
    }, [assetReservations])

    const pastReservations = useMemo<{ [x: number]: AssetReservationPaged[] } | null>(() => {
        const now = nowWithoutTimezone()
        const upcomingItems: { [x: number]: AssetReservationPaged[] } | undefined = assetReservations
            ?.filter(ev => ev.eventEnd?.isBefore(now))
            ?.sort((a, b) => b.eventStart!.diff(a.eventStart, "minutes"))
            ?.reduce((all, item) => {
                if (!item.eventEnd) return all
                all[item.eventEnd.year()] = all[item.eventEnd.year()] || []
                all[item.eventEnd.year()].push(item)
                return all
            }, Object.create(null))
        return upcomingItems || null
    }, [assetReservations])

    const pastReservationYears = useMemo(() => {
        if (!pastReservations) return []
        return Object.keys(pastReservations).sort((a, b) => +b - +a)
    }, [pastReservations])

    const [pastReservationExpandedYears, setPastReservationExpandedYears] = useState<number[]>([])

    function handlePastReservationsYearToggle(expanded: boolean, sectionId: string) {
        if (expanded) {
            setPastReservationExpandedYears([...pastReservationExpandedYears, +sectionId])
            return
        }

        setPastReservationExpandedYears(pastReservationExpandedYears.filter(year => year !== +sectionId))
    }

    const upcomingReservations = useMemo<AssetReservationPaged[] | null>(() => {
        const now = nowWithoutTimezone()
        const upcomingItems = assetReservations?.filter(ev => ev.eventStart?.isAfter(now))
        return upcomingItems && upcomingItems.length > 0 ? upcomingItems : null
    }, [assetReservations])

    const handleDeleteReservation = useCallback(() => {
        reloadReservations()
        if (typeof onDeleteReservation === "function") {
            onDeleteReservation()
        }
    }, [onDeleteReservation, reloadReservations])

    const handleReservationClick = useCallback((reservationId: Guid, isPast?: boolean) => {
        reservationSidebars.show({
            reservationId,
            viewOnly: isPast || readOnly,
            onDelete: readOnly ? undefined : handleDeleteReservation
        })
    }, [handleDeleteReservation, readOnly])

    const handleInspectionExportClick = useCallback(async (reservationId: Guid, deliveryInspectionId?: Guid, returnInspectionId?: Guid) => {
        try {
            await AssetService.exportAssetInspection({
                assetId: effectiveAssetId,
                reservationId,
                deliveryInspectionId,
                returnInspectionId
            })
        } catch (e) {
            console.error(e)
            toastMessages.showSystemError()
        }
    }, [effectiveAssetId])

    const scheduleNewRequestButton = canCreateReservation ? (
        <Button
            type="link"
            url={Routes.reservationCreate.getPath({}, { assetId: asset.id, articleId: asset.articleId, backUrl: location.pathname + location.search })}
            theme="secondary"
            label={t('requests.scheduleNewRequest')}
        />
    ) : null

    const latestCondition = asset.condition
    const latestFuel = globalLatestAssetInspection?.fuel
    const latestOperationHours = asset.operationHours ?? undefined
    const latestMileage = globalLatestAssetInspection?.mileage ?? undefined
    const latestAdBlue = globalLatestAssetInspection?.adBlue ?? undefined

    return (
        <>
            {(!assetReservations || (assetReservations.length && !assetInspections)) && !isLoadingFailed
                ? <div className="my-6">
                    <Spinner />
                </div>
                : <>
                    {!!assetReservations && assetReservations.length > 0 &&
                        <Grid
                            columns="1fr"
                            gap="4.8rem">
                            {canCreateReservation &&
                                <GridItem>
                                    {scheduleNewRequestButton}
                                </GridItem>
                            }
                            {!!currentReservation &&
                                <GridItem>
                                    <Text type="h3" className="mb-3">{t('asset.currentRequest')}</Text>
                                    <div className="mb-2">
                                        <SchedulerEvent
                                            assetId={effectiveAssetId}
                                            reservationId={currentReservation.event.id}
                                            status={currentReservation.event.status}
                                            type={getSchedulerEventTypeByReservationPurpose(currentReservation.event.purpose)}
                                            title={[`${getPurposeLabel(currentReservation.event.purpose)} (${currentReservation.event.number})`, currentReservation.event.customerName].join(", ")}
                                            descr={currentReservation.event.eventAddress?.print()}
                                            startDate={currentReservation.event.eventStart}
                                            endDate={currentReservation.event.eventEnd}
                                            progress={currentReservation.progress}
                                            nowLine
                                            isLocked={currentReservation.event.hasUserAssignment}
                                            latestCondition={latestCondition}
                                            latestFuel={latestFuel}
                                            latestOperationHours={latestOperationHours}
                                            latestMileage={latestMileage}
                                            latestAdBlue={latestAdBlue}
                                            comment={currentReservation.event.comment}
                                            forceInspectionExpanded
                                            onClick={() => handleReservationClick(currentReservation.event.id)}
                                            linkGetReservationShippingOrders={currentReservation.event.linkGetReservationShippingOrders}
                                            {...getLatestInspections(assetInspectionsGrouped?.get(currentReservation.event.id) || [])}
                                            onInspectionCreated={fetchAssetInspections}
                                            onInspectionExportClick={(deliveryInspectionId, returnInspectionId) => handleInspectionExportClick(currentReservation.event.id, deliveryInspectionId, returnInspectionId)}
                                            isMachine={isMachine}
                                        />
                                    </div>
                                </GridItem>
                            }
                            {!!upcomingReservations && upcomingReservations.length > 0 &&
                                <GridItem>
                                    <Text type="h3" className="mb-3">{t('asset.upcomingRequests')}</Text>
                                    {upcomingReservations.map((event, index) => (
                                        <div
                                            key={index}
                                            className="mb-2">
                                            <SchedulerEvent
                                                assetId={effectiveAssetId}
                                                reservationId={event.id}
                                                status={event.status}
                                                type={getSchedulerEventTypeByReservationPurpose(event.purpose)}
                                                title={[`${getPurposeLabel(event.purpose)} (${event.number})`, event.customerName].join(", ")}
                                                descr={event.eventAddress?.print()}
                                                startDate={event.eventStart}
                                                endDate={event.eventEnd}
                                                isLocked={event.hasUserAssignment}
                                                linkGetReservationShippingOrders={event.linkGetReservationShippingOrders}
                                                latestCondition={latestCondition}
                                                latestFuel={latestFuel}
                                                latestOperationHours={latestOperationHours}
                                                latestMileage={latestMileage}
                                                latestAdBlue={latestAdBlue}
                                                comment={event.comment}
                                                onClick={() => handleReservationClick(event.id)}
                                                {...getLatestInspections(assetInspectionsGrouped?.get(event.id) || [])}
                                                onInspectionCreated={fetchAssetInspections}
                                                onInspectionExportClick={(deliveryInspectionId, returnInspectionId) => handleInspectionExportClick(event.id, deliveryInspectionId, returnInspectionId)}
                                                isMachine={isMachine}
                                            />
                                        </div>
                                    ))}
                                </GridItem>
                            }
                            {!!pastReservations && pastReservationYears.length > 0 &&
                                <GridItem>
                                    <Text type="h3" className="mb-1">{t('asset.pastRequests')}</Text>
                                    {pastReservationYears.map((yearKey, yearIndex) => (
                                        <div
                                            key={yearIndex}
                                            className="pt-2 mb-2 border-grey-100-between">
                                            <SettingsBlock
                                                title={<Flex justifyContent="space-between" alignItems="center" className="w-100">
                                                    <FlexItem>
                                                        <Text type="h4">{yearKey}</Text>
                                                    </FlexItem>
                                                    <FlexItem><Text type="p" className="font-regular">{t('common.xRequests', { count: pastReservations[+yearKey].length })}</Text></FlexItem>
                                                </Flex>}
                                                expanded={!!pastReservationExpandedYears.includes(+yearKey)}
                                                expandableId={yearKey}
                                                onToggle={handlePastReservationsYearToggle}
                                                wrapperClass="">
                                                {pastReservations[+yearKey].map((event, eventIndex) => (
                                                    <div
                                                        key={eventIndex}
                                                        className="mb-2">
                                                        <SchedulerEvent
                                                            assetId={effectiveAssetId}
                                                            reservationId={event.id}
                                                            status={event.status}
                                                            type={getSchedulerEventTypeByReservationPurpose(event.purpose)}
                                                            title={[`${getPurposeLabel(event.purpose)} (${event.number})`, event.customerName].join(", ")}
                                                            descr={event.eventAddress?.print()}
                                                            startDate={event.eventStart}
                                                            endDate={event.eventEnd}
                                                            latestCondition={latestCondition}
                                                            latestFuel={latestFuel}
                                                            latestOperationHours={latestOperationHours}
                                                            latestMileage={latestMileage}
                                                            latestAdBlue={latestAdBlue}
                                                            comment={event.comment}
                                                            onClick={() => handleReservationClick(event.id, true)}
                                                            linkGetReservationShippingOrders={event.linkGetReservationShippingOrders}
                                                            {...getLatestInspections(assetInspectionsGrouped?.get(event.id) || [])}
                                                            onInspectionCreated={fetchAssetInspections}
                                                            onInspectionExportClick={(deliveryInspectionId, returnInspectionId) => handleInspectionExportClick(event.id, deliveryInspectionId, returnInspectionId)}
                                                            isMachine={isMachine}
                                                        />
                                                    </div>
                                                ))}
                                            </SettingsBlock>
                                        </div>
                                    ))}
                                </GridItem>
                            }
                        </Grid>
                    }
                    {(isLoadingFailed || (assetReservations && assetReservations.length === 0)) &&
                        <div className="text-center py-6">
                            <img src={noEventsImg} alt="" className="mb-4" />
                            <Text type="p" className="white-space-pre-line mb-4">{t(canCreateReservation ? 'asset.noRequestsScheduledScheduleNew' : 'asset.noRequestsScheduled')}</Text>
                            {canCreateReservation && scheduleNewRequestButton}
                        </div>
                    }
                </>
            }
        </>
    )
}

export default Reservations
